/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving an individual patient.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

 /*
  * Used to register the logics so that they are included in the store. 
  */
import reducerRegistry from '../ReducerRegistry';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialAppState {
    specification: string | null;
    specificationTermId: number | null;
    targetTermId: number | null;
    minGrade: number | null;
    maxGrade: number | null;
    allowUnknownGrade: boolean;
}

export interface IAppState {
   app: IInitialAppState;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const AppInitialState: IInitialAppState = {
    specification: null,
    specificationTermId: null,
    targetTermId: null,
    minGrade: null,
    maxGrade: null,
    allowUnknownGrade: false
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useAppSelector: TypedUseSelectorHook<IAppState> = useSelector;

export const AppSelectors = {
    selectSpecification: (state: IAppState, termId: number | null) => termId && state.app.specificationTermId && state.app.specificationTermId === termId ? state.app.specification : null,
    selectSpecifcationTermId: (state: IAppState) => state.app.specificationTermId,
    selectTargetTermId: (state: IAppState) => state.app.targetTermId,
    selectMaxGrade: (state: IAppState) => state.app.maxGrade,
    selectMinGrade: (state: IAppState) => state.app.minGrade,
    selectAllowUnknownGrade: (state: IAppState) => state.app.allowUnknownGrade
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class AppReducer extends ImmerReducer<IInitialAppState> {
    public setSpecification(termId: number | null, specification: string | null) {
        this.draftState.specification = specification;
        this.draftState.specificationTermId = termId;
    }

    public setTargetTermId(termId: number | null) {
        this.draftState.targetTermId = termId;
    }

    public setMaxGrade(maxGrade: number | null) {
        this.draftState.maxGrade = maxGrade;
    }

    public setMinGrade(minGrade: number | null) {
        this.draftState.minGrade = minGrade;
    }

    public setAllowUnknownGrade(allow: boolean) {
        this.draftState.allowUnknownGrade = allow;
    }

    public clear(appId: number) {
        return {...AppInitialState};
    }
}

export const AppActions = createActionCreators(AppReducer);
const reducer = createReducerFunction(AppReducer, AppInitialState);

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('app', reducer);


// WEBPACK FOOTER //
// ./src/store/reducers/app.ts